'use client';

import { Flex, Menu, Portal, Text } from '@chakra-ui/react';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { HiChevronUpDown } from 'react-icons/hi2';
import { LuBriefcase, LuLandmark, LuTrendingUp, LuTruck } from 'react-icons/lu';

import { useActivePersonaMode } from 'hooks/useActivePersonaMode';
import { useOrgSlug } from 'hooks/useOrgSlug';
import { toPxString } from 'helpers/styles';
import { writePersonaModeCookie } from 'helpers/personaModeCookie';
import {
  DEFAULT_PERSONA_MODE,
  PERSONA_MODE_META,
  PERSONA_MODE_ORDER,
  PersonaMode,
} from '@features/navigation/personaMode';

const MODE_PICKER_MAX_WIDTH_PX = 300;

const MODE_ICONS: Record<PersonaMode, React.ReactNode> = {
  [PersonaMode.Analyst]: <LuTrendingUp size="18px" />,
  [PersonaMode.Operator]: <LuBriefcase size="18px" />,
  [PersonaMode.Supplier]: <LuTruck size="18px" />,
  [PersonaMode.Finance]: <LuLandmark size="18px" />,
};

const ModePicker = () => {
  const orgSlug = useOrgSlug();
  const router = useRouter();
  const activeMode = useActivePersonaMode();

  const handleModeChange = useCallback(
    (mode: PersonaMode) => () => {
      if (mode === activeMode || orgSlug == null) {
        return;
      }
      // Persist eagerly for the org-root redirect; ModeSync also mirrors the URL.
      writePersonaModeCookie(mode);
      router.push(`/${orgSlug}/${mode}/positions`);
    },
    [activeMode, orgSlug, router],
  );

  const activeMeta = PERSONA_MODE_META[activeMode] ?? PERSONA_MODE_META[DEFAULT_PERSONA_MODE];

  return (
    <Menu.Root positioning={{ placement: 'bottom-start' }}>
      <Menu.Trigger asChild>
        <Flex cursor="pointer" alignItems="center" gap={2} w="full">
          <Flex
            alignItems="center"
            justifyContent="center"
            boxSize="28px"
            borderRadius="md"
            bgColor="surface.hover"
            color="text.secondary"
            flexShrink={0}
          >
            {MODE_ICONS[activeMode]}
          </Flex>
          <Flex flexDirection="column" w="full">
            <Text color="text.secondary" fontWeight="medium" textAlign="left" fontSize="xs">
              Mode
            </Text>
            <Text fontWeight="medium" textAlign="left" fontSize="sm">
              {activeMeta.label}
            </Text>
          </Flex>
          <HiChevronUpDown size="24px" />
        </Flex>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner w={toPxString(MODE_PICKER_MAX_WIDTH_PX)}>
          <Menu.Content bgColor="surface.background" p={2} fontWeight="medium" borderRadius="lg">
            <Text fontSize="sm" color="text.secondary" mb={2}>
              Switch mode
            </Text>
            {PERSONA_MODE_ORDER.map((mode) => {
              const meta = PERSONA_MODE_META[mode];
              return (
                <Menu.CheckboxItem
                  key={mode}
                  value={mode}
                  checked={activeMode === mode}
                  onCheckedChange={handleModeChange(mode)}
                  cursor="pointer"
                  fontSize="sm"
                  _highlighted={{ bg: 'surface.hover' }}
                  borderRadius="md"
                >
                  <Flex alignItems="center" gap={2}>
                    {MODE_ICONS[mode]}
                    <Flex flexDirection="column">
                      <Text>{meta.label}</Text>
                      <Text fontSize="xs" color="text.secondary" fontWeight="normal">
                        {meta.description}
                      </Text>
                    </Flex>
                  </Flex>
                  <Menu.ItemIndicator color="selection.500" />
                </Menu.CheckboxItem>
              );
            })}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};

export default ModePicker;
